import { useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "../components/Icon.jsx";
import { DriverActionDialog } from "./DriverActionDialog.jsx";
import { DriverCameraField } from "./DriverCameraField.jsx";
import { DriverTaskOperations } from "./DriverOperations.jsx";
import { captureDriverLocation } from "./location.js";
import {
  listDriverStopCompletionMedia,
  repairDriverMediaContext,
  subscribeDriverSync,
  supersedeDriverMedia,
} from "./offline-store.js";

const DELIVERY_ISSUES = [
  ["GOODS_SHORT", "Barang kurang"],
  ["GOODS_EXCESS", "Barang lebih"],
  ["GOODS_SWAPPED", "Barang tertukar"],
  ["GOODS_DAMAGED", "Barang rusak"],
  ["GOODS_LEAKING", "Barang bocor"],
  ["DOCUMENT_MISMATCH", "Dokumen tidak sesuai"],
  ["PARTIAL_REJECTION", "Sebagian barang ditolak"],
  ["QUANTITY_MISMATCH", "Selisih jumlah"],
  ["ADMINISTRATION", "Kendala administrasi"],
  ["OTHER", "Lainnya"],
];

const FAILURE_REASONS = [
  ["STORE_CLOSED", "Toko tutup"],
  ["CUSTOMER_UNAVAILABLE", "Pelanggan tidak berada di lokasi"],
  ["CUSTOMER_CANCELLED", "Pelanggan membatalkan"],
  ["SALES_CANCELLED", "Sales membatalkan"],
  ["CUSTOMER_REJECTED", "Pelanggan menolak barang"],
  ["ADDRESS_NOT_FOUND", "Alamat tidak ditemukan"],
  ["ACCESS_BLOCKED", "Kendaraan tidak dapat mengakses lokasi"],
  ["DOCUMENT_MISMATCH", "Dokumen tidak sesuai"],
  ["GOODS_PROBLEM", "Barang bermasalah"],
  ["RESCHEDULE_REQUESTED", "Permintaan dijadwalkan ulang"],
  ["VEHICLE_PROBLEM", "Kendala kendaraan"],
  ["WEATHER", "Kendala cuaca"],
  ["ROAD_PROBLEM", "Kendala jalan"],
  ["OTHER", "Lainnya"],
];

const FINAL_STOP_STATUSES = new Set(["COMPLETED", "COMPLETED_WITH_ISSUE", "FAILED", "CANCELLED"]);

const FOLLOW_UPS = [
  ["RETRY_TODAY", "Coba kembali hari ini"],
  ["CONTINUE_TOMORROW", "Lanjutkan besok"],
  ["WAIT_ADMIN", "Menunggu arahan admin"],
  ["RETURN_WAREHOUSE", "Kembalikan ke gudang"],
  ["FINAL", "Gagal final"],
];

function statusTone(status) {
  const normalized = String(status || "").toUpperCase();
  if (normalized === "IN_PROGRESS" || normalized === "EN_ROUTE") return "active";
  if (normalized === "COMPLETED" || normalized === "COMPLETED_WITH_ISSUE") return "success";
  if (normalized === "FAILED" || normalized === "CANCELLED") return "danger";
  if (normalized === "DEFERRED") return "warning";
  return "neutral";
}

function statusIcon(status) {
  const normalized = String(status || "PENDING").toUpperCase();
  if (normalized === "COMPLETED") return "check";
  if (normalized === "COMPLETED_WITH_ISSUE") return "warning";
  if (normalized === "EN_ROUTE") return "route";
  if (normalized === "FAILED" || normalized === "CANCELLED") return "close";
  if (normalized === "DEFERRED") return "clock";
  return null;
}

function stopStatusLabel(status) {
  const normalized = String(status || "PENDING").toUpperCase();
  if (normalized === "COMPLETED") return "Selesai";
  if (normalized === "COMPLETED_WITH_ISSUE") return "Selesai dengan kendala";
  if (normalized === "EN_ROUTE") return "Sedang menuju";
  if (normalized === "FAILED") return "Gagal";
  if (normalized === "CANCELLED") return "Dibatalkan";
  if (normalized === "DEFERRED") return "Ditunda";
  return "Belum dijalankan";
}

function formatRouteDistance(value) {
  const meters = Number(value);
  if (!Number.isFinite(meters) || meters <= 0) return null;
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toLocaleString("id-ID", { maximumFractionDigits: 1 })} km`;
}

function formatRouteDuration(value) {
  const seconds = Number(value);
  if (!Number.isFinite(seconds) || seconds <= 0) return null;
  const minutes = Math.max(1, Math.round(seconds / 60));
  if (minutes < 60) return `±${minutes} menit`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return remainder ? `±${hours} jam ${remainder} menit` : `±${hours} jam`;
}

function routeLegForStop(routeSummary, stopKey) {
  if (!stopKey || !Array.isArray(routeSummary?.legs)) return null;
  return routeSummary.legs.find((leg) => String(leg?.toKey || "") === String(stopKey)) || null;
}

function googleMapsUrl(stop) {
  const latitude = Number(stop?.latitude);
  const longitude = Number(stop?.longitude);
  if (!Number.isFinite(latitude) || latitude < -90 || latitude > 90) return null;
  if (!Number.isFinite(longitude) || longitude < -180 || longitude > 180) return null;
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${latitude},${longitude}`)}`;
}

function FloatingRouteAction({ count, busy, canReorder, onOpen }) {
  if (!canReorder || count <= 0) return null;
  return (
    <button
      aria-label={`Atur rute untuk ${count} titik tersisa`}
      className="driver-route-floating-action"
      disabled={busy}
      onClick={onOpen}
      type="button"
    >
      <Icon name="route" size={20} />
      <span>Atur rute</span>
    </button>
  );
}

function LoadingPreparation({ stops, execution, task, canEdit, canConfirm, busy, guided = false, onSave, onConfirm }) {
  const [readyByKey, setReadyByKey] = useState(() => Object.fromEntries(stops.map((stop) => [stop.key, Boolean(stop.loading?.ready)])));

  useEffect(() => {
    setReadyByKey(Object.fromEntries(stops.map((stop) => [stop.key, Boolean(stop.loading?.ready)])));
  }, [stops]);

  const readyCount = stops.filter((stop) => readyByKey[stop.key]).length;
  const allReady = stops.length > 0 && readyCount === stops.length;
  const serverReadyCount = Number(execution?.loadingReadyCount || 0);
  const changed = stops.some((stop) => Boolean(stop.loading?.ready) !== Boolean(readyByKey[stop.key]));

  function payload() {
    return {
      checks: stops.map((stop) => ({
        stopKey: stop.key,
        ready: Boolean(readyByKey[stop.key]),
        issueCode: null,
        notes: null,
      })),
      expectedRouteVersion: Number(execution?.routeVersion || 1),
      expectedStopKeys: stops.map((stop) => stop.key),
      clientSavedAt: new Date().toISOString(),
    };
  }

  async function confirmLoading() {
    if (changed || serverReadyCount !== stops.length) await onSave(payload());
    await onConfirm({
      expectedRouteVersion: Number(execution?.routeVersion || 1),
      expectedStopKeys: stops.map((stop) => stop.key),
      clientSavedAt: new Date().toISOString(),
    });
  }

  return (
    <section className={`driver-section driver-preparation-card driver-loading-stage ${guided ? "is-guided" : ""}`}>
      <div className="driver-loading-context" aria-label="Konteks Fleet Order">
        <strong>{task?.foNumber || "Fleet Order"}</strong>
        <span>{task?.routeName || task?.depotName || "Rute belum tersedia"}</span>
        <span>{stops.length} pekerjaan</span>
      </div>
      <div className="driver-section-heading driver-guided-heading">
        <div>
          {guided && <span className="driver-guided-step">Tahap 1 dari 3</span>}
          <h2>1. Konfirmasi loading</h2>
          <p>Centang setiap pekerjaan setelah barang dan dokumennya siap dimuat.</p>
        </div>
        <strong className="driver-loading-progress">{readyCount}/{stops.length}</strong>
      </div>

      {canEdit && (
        <button
          className="driver-compact-action"
          disabled={busy}
          onClick={() => setReadyByKey(Object.fromEntries(stops.map((stop) => [stop.key, true])))}
          type="button"
        >
          Tandai semua siap
        </button>
      )}

      <div className="driver-loading-list">
        {stops.map((stop) => (
          <label className={`driver-loading-row ${readyByKey[stop.key] ? "is-ready" : ""}`} key={stop.key}>
            <input
              checked={Boolean(readyByKey[stop.key])}
              disabled={!canEdit || busy}
              onChange={(event) => setReadyByKey((current) => ({ ...current, [stop.key]: event.target.checked }))}
              type="checkbox"
            />
            <span className="driver-loading-sequence">{stop.sequence}</span>
            <span className="driver-loading-copy">
              <strong>{stop.customerName}</strong>
              <small>{readyByKey[stop.key] ? "Barang dan dokumen siap" : "Belum dikonfirmasi"}</small>
            </span>
            {readyByKey[stop.key] && <Icon name="check" size={17} />}
          </label>
        ))}
      </div>

      {canEdit && (
        <>
          <p className={`driver-loading-helper ${allReady ? "is-ready" : ""}`} role="status">
            {allReady ? "Semua pekerjaan siap. Loading dapat dikonfirmasi." : `${readyCount}/${stops.length} pekerjaan siap · Centang seluruh pekerjaan untuk melanjutkan.`}
          </p>
          <div className="driver-inline-actions driver-loading-actions">
            <button className="driver-secondary-button" disabled={!changed || busy} onClick={() => onSave(payload())} type="button">
              Simpan progres
            </button>
            <button
              className="driver-primary-button"
              disabled={!allReady || !canConfirm || busy}
              onClick={confirmLoading}
              type="button"
            >
              Konfirmasi loading
            </button>
          </div>
        </>
      )}

      {!canEdit && execution?.loadingConfirmedAt && (
        <div className="driver-success-note"><Icon name="check" size={17} /> Loading sudah dikonfirmasi.</div>
      )}
    </section>
  );
}

function StartTripCard({ busy, onStart }) {
  const [acknowledged, setAcknowledged] = useState(false);
  return (
    <section className="driver-start-trip-card">
      <span className="driver-placeholder-icon"><Icon name="truck" size={26} /></span>
      <div>
        <span className="driver-eyebrow">Siap berangkat</span>
        <h2>Berangkat dari depot</h2>
        <p>Mulai perjalanan hanya setelah pengecekan kendaraan dan loading selesai.</p>
      </div>
      <label className="driver-acknowledgement">
        <input checked={acknowledged} onChange={(event) => setAcknowledged(event.target.checked)} type="checkbox" />
        <span>Saya memastikan pengecekan kendaraan sudah dilakukan.</span>
      </label>
      <button className="driver-primary-button" disabled={!acknowledged || busy} onClick={() => onStart(acknowledged)} type="button">
        <Icon name="route" size={18} /> Berangkat dari Depot
      </button>
    </section>
  );
}

export function DeliveryDialog({ mode, stop, fleetOrderId, fleetOrderNumber, vehiclePlate, currentUser, stopSequence, stopTotal, busy, onClose, onSubmit }) {
  const [outcome, setOutcome] = useState("NO_ISSUE");
  const [issueCode, setIssueCode] = useState("");
  const [reason, setReason] = useState("");
  const [followUp, setFollowUp] = useState("");
  const [notes, setNotes] = useState("");
  const [photos, setPhotos] = useState([]);

  const complete = mode === "complete";
  const valid = complete
    ? (outcome === "NO_ISSUE" || Boolean(issueCode)) && photos.length >= 1 && photos.every((photo) => photo.status !== "FAILED")
    : Boolean(reason && followUp);

  return (
    <DriverActionDialog
      actions={(
        <>
          <button className="driver-secondary-button" disabled={busy} onClick={onClose} type="button">Batal</button>
          <button
            className={complete ? "driver-primary-button" : "driver-danger-button"}
            disabled={!valid || busy}
            onClick={() => onSubmit(complete ? { outcome, issueCode: outcome === "ISSUE" ? issueCode : null, notes, photoRefs: photos.map((photo) => photo.ref) } : { reason, followUp, notes })}
            type="button"
          >
            {busy ? "Menyimpan..." : complete ? "Selesaikan delivery" : "Simpan hasil gagal"}
          </button>
        </>
      )}
      busy={busy}
      description={stop.customerName}
      onClose={onClose}
      title={complete ? "Hasil delivery" : "Delivery gagal atau ditunda"}
    >
      {complete ? (
        <div className="driver-form-stack">
          <div className="driver-choice-grid">
            <label className={outcome === "NO_ISSUE" ? "is-selected" : ""}>
              <input checked={outcome === "NO_ISSUE"} onChange={() => setOutcome("NO_ISSUE")} type="radio" />
              <strong>Tidak ada masalah</strong>
            </label>
            <label className={outcome === "ISSUE" ? "is-selected" : ""}>
              <input checked={outcome === "ISSUE"} onChange={() => setOutcome("ISSUE")} type="radio" />
              <strong>Ada kendala</strong>
            </label>
          </div>
          {outcome === "ISSUE" && (
            <label><span>Jenis kendala</span><select onChange={(event) => setIssueCode(event.target.value)} required value={issueCode}><option value="">Pilih kendala</option>{DELIVERY_ISSUES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
          )}
          <CompletionPhotosField
            currentUser={currentUser}
            fleetOrderId={fleetOrderId}
            fleetOrderNumber={fleetOrderNumber}
            onChange={setPhotos}
            photos={photos}
            stopKey={stop.key}
            stopName={stop.customerName}
            stopSequence={stopSequence}
            stopTotal={stopTotal}
            vehiclePlate={vehiclePlate}
          />
          <label><span>Keterangan</span><textarea onChange={(event) => setNotes(event.target.value)} placeholder="Opsional" rows={3} value={notes} /></label>
        </div>
      ) : (
        <div className="driver-form-stack">
          <label><span>Alasan</span><select onChange={(event) => setReason(event.target.value)} value={reason}><option value="">Pilih alasan</option>{FAILURE_REASONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
          <label><span>Tindak lanjut</span><select onChange={(event) => setFollowUp(event.target.value)} value={followUp}><option value="">Pilih tindak lanjut</option>{FOLLOW_UPS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
          <label><span>Keterangan</span><textarea onChange={(event) => setNotes(event.target.value)} placeholder="Jelaskan kondisi singkat" rows={3} value={notes} /></label>
        </div>
      )}
    </DriverActionDialog>
  );
}

function CompletionPhotosField({ fleetOrderId, fleetOrderNumber, stopKey, stopName, stopSequence, stopTotal, vehiclePlate, currentUser, photos, onChange }) {
  const [error, setError] = useState("");
  const photosRef = useRef(photos);
  useEffect(() => { photosRef.current = photos; }, [photos]);
  useEffect(() => () => photosRef.current.forEach((photo) => {
    if (photo.preview) URL.revokeObjectURL(photo.preview);
  }), []);

  useEffect(() => {
    let mounted = true;
    async function hydrateStoredPhotos() {
      const stored = await listDriverStopCompletionMedia({ fleetOrderId, stopKey });
      if (!mounted) return;
      const currentById = new Map(photosRef.current.map((photo) => [photo.id, photo]));
      const next = stored.slice(-10).map((item) => {
        const existing = currentById.get(item.id);
        const status = item.syncStatus === "SYNCED"
          ? "SYNCED"
          : String(item.syncStatus || "").startsWith("FAILED") ? "FAILED" : "PENDING";
        if (existing) {
          return {
            ...existing,
            ref: item.serverMediaId || item.id,
            status,
          };
        }
        return {
          id: item.id,
          ref: item.serverMediaId || item.id,
          preview: item.file ? URL.createObjectURL(item.file) : null,
          size: Number(item.file?.size || item.byteSize || 0),
          status,
        };
      });
      const signature = (items) => items.map((item) => `${item.id}:${item.ref}:${item.status}`).join("|");
      if (signature(next) !== signature(photosRef.current)) onChange(next);
    }
    hydrateStoredPhotos().catch(() => {});
    const unsubscribe = subscribeDriverSync(() => hydrateStoredPhotos().catch(() => {}));
    return () => {
      mounted = false;
      unsubscribe();
    };
  }, [fleetOrderId, stopKey, onChange]);

  async function addPhoto(ref, media) {
    if (photos.length >= 10) {
      setError("Maksimal 10 foto untuk satu penyelesaian titik.");
      return;
    }
    if (!fleetOrderId || !stopKey) {
      throw new Error("Fleet Order atau titik aktif belum tersedia. Muat ulang detail tugas sebelum mengambil foto.");
    }
    setError("");
    await repairDriverMediaContext(media.localId, {
      fleetOrderId,
      stopKey,
      ownerType: "DELIVERY_STOP",
    });
    const next = [...photos, {
      id: media.localId,
      ref,
      preview: URL.createObjectURL(media.file),
      size: media.byteSize,
      status: media.status,
    }];
    onChange(next);
  }

  async function removePhoto(photo, index) {
    if (!window.confirm(`Batalkan foto bukti ${index + 1}? Foto tidak akan dipakai untuk penyelesaian titik ini.`)) return;
    await supersedeDriverMedia(photo.id);
    URL.revokeObjectURL(photo.preview);
    onChange(photos.filter((item) => item.id !== photo.id));
  }

  return (
    <fieldset className="driver-completion-photos">
      <legend>Foto bukti penyelesaian</legend>
      <span className="driver-photo-counter">{photos.length} dari 10 foto</span>
      <DriverCameraField
        clearAfterUse
        disabled={photos.length >= 10}
        label="Foto bukti"
        mediaKind="STOP_COMPLETION"
        metadata={{
          ownerType: "DELIVERY_STOP",
          fleetOrderId,
          stopKey,
          driverName: currentUser?.displayName,
          fleetOrderNumber,
          stopName,
          stopSequence,
          stopTotal,
          vehiclePlate,
          activityLabel: "Bukti penyelesaian pengiriman",
        }}
        onChange={addPhoto}
      />
      <div className="driver-photo-preview-grid">
        {photos.map((photo, index) => (
          <article key={photo.id}>
            {photo.preview ? <img alt={`Foto bukti ${index + 1}`} src={photo.preview} /> : <span className="driver-photo-preview-placeholder"><Icon name="camera" size={22} /> Foto tersimpan</span>}
            <small>{Math.round(photo.size / 1024)} KB · {photo.status === "SYNCED" ? "Tersinkron" : "Menunggu sinkronisasi"}</small>
            <button aria-label={`Batalkan foto ${index + 1}`} onClick={() => removePhoto(photo, index)} type="button">Batalkan</button>
          </article>
        ))}
      </div>
      {error && <small className="driver-field-error">{error}</small>}
      {!photos.length && <small>Minimal 1 foto wajib dilampirkan.</small>}
    </fieldset>
  );
}

function DriverAmendmentAlert({ amendment, busy, onAcknowledge }) {
  if (!amendment) return null;
  const waitingAcknowledgement = amendment.status === "PENDING_ACK";
  const documents = Array.isArray(amendment.documents) ? amendment.documents : [];

  return (
    <section className={`driver-amendment-alert ${waitingAcknowledgement ? "is-urgent" : "is-loading"}`}>
      <span className="driver-amendment-alert__icon" aria-hidden="true">
        <Icon name="warning" size={22} />
      </span>
      <div className="driver-amendment-alert__content">
        <span className="driver-eyebrow">Fleet Order diperbarui</span>
        <h2>
          {amendment.addedDocumentCount} Sales Order tambahan
          {amendment.addedStopCount > 0 ? ` · ${amendment.addedStopCount} stop baru` : ""}
        </h2>
        <p>{amendment.reason}</p>
        <div className="driver-amendment-impact">
          <span><strong>{amendment.before.documentCount}</strong> → <strong>{amendment.after.documentCount}</strong> dokumen</span>
          <span><strong>{amendment.before.stopCount}</strong> → <strong>{amendment.after.stopCount}</strong> stop</span>
        </div>
        {documents.length > 0 && (
          <div className="driver-amendment-documents">
            {documents.map((document) => (
              <span key={document.id}>
                <strong>{document.documentNumber}</strong>
                <small>{document.customerName || "Pelanggan"}</small>
              </span>
            ))}
          </div>
        )}
        <p className="driver-amendment-instruction">
          {waitingAcknowledgement
            ? "Periksa perubahan lalu konfirmasikan bahwa informasi sudah diterima."
            : "Perubahan sudah diterima. Periksa dan konfirmasikan kembali loading stop terdampak sebelum perjalanan dilanjutkan."}
        </p>
      </div>
      {waitingAcknowledgement ? (
        <button
          className="driver-primary-button"
          disabled={busy}
          onClick={() => onAcknowledge(amendment.id)}
          type="button"
        >
          <Icon name="check" size={18} /> Konfirmasi Diterima
        </button>
      ) : (
        <span className="driver-amendment-alert__status">Loading ulang diperlukan</span>
      )}
    </section>
  );
}

export function DriverTaskDetail({
  currentUser,
  detail,
  error,
  loading,
  mapLoading,
  routeWorkspaceLoading,
  actionBusy,
  onOpenMap,
  onOpenRouteWorkspace,
  onMakeNext,
  onSaveLoading,
  onConfirmLoading,
  onAcknowledgeAmendment,
  onStartStop,
  onCompleteStop,
  onFailStop,
  onOperationalChanged,
  onContinueTrip,
  onRetryDetail,
  unsyncedCount,
}) {
  const [dialog, setDialog] = useState(null);
  const [selectedStopPreview, setSelectedStopPreview] = useState(null);
  const [nextChangeTarget, setNextChangeTarget] = useState(null);

  if (loading) return <div className="driver-inline-loading"><span className="loading-spinner" /> Memuat detail Fleet Order...</div>;
  if (error) return <div className="driver-error-banner">{error}</div>;
  if (!detail?.task) {
    return (
      <section className="driver-detail-empty-state" role="status">
        <span className="driver-placeholder-icon"><Icon name="warning" size={26} /></span>
        <strong>Detail Fleet Order belum termuat</strong>
        <p>Data tugas belum berhasil ditampilkan. Muat ulang detail tanpa menghapus data atau foto yang tersimpan di perangkat.</p>
        <button className="driver-primary-button" onClick={onRetryDetail} type="button">Muat ulang detail</button>
      </section>
    );
  }

  const { task, stops, execution, capabilities, amendment } = detail;
  const activeStop = stops.find((stop) => stop.status === "EN_ROUTE") || null;
  const nextStop = activeStop || stops.find((stop) => new Set(["PENDING", "DEFERRED"]).has(stop.status)) || null;
  const allStopsFinal = stops.length > 0 && stops.every((stop) => FINAL_STOP_STATUSES.has(String(stop.status || "PENDING").toUpperCase()));
  const phase = execution?.phase || "PREPARATION";
  const guidedLoadingPhase = new Set(["PREPARATION", "LOADING"]).has(phase);
  const guidedDeparturePhase = phase === "READY_TO_DEPART";
  const emergencyLoadingPhase = phase === "IN_PROGRESS" && (capabilities?.canEditLoading || capabilities?.canConfirmLoading);
  const tripExecutionPhase = phase === "IN_PROGRESS";
  const overnight = detail.operations?.overnight || null;
  const stopExecutionPaused = Boolean(overnight);
  const morningReportProminent = Boolean(detail.deliveryReporting?.morningRequired) && !stopExecutionPaused;
  const promoteOperations = allStopsFinal && !stopExecutionPaused;
  const showOperationsEarly = guidedDeparturePhase || morningReportProminent;
  const remainingRouteCount = stops.filter((stop) => new Set(["PENDING", "DEFERRED"]).has(String(stop.status || "PENDING").toUpperCase())).length;
  const nextRouteLeg = routeLegForStop(detail.routeSummary, nextStop?.key);
  const nextDistanceLabel = formatRouteDistance(nextRouteLeg?.distanceM);
  const nextDurationLabel = formatRouteDuration(nextRouteLeg?.durationS);
  const nextRouteMetricLabel = [nextDistanceLabel, nextDurationLabel].filter(Boolean).join(" · ");
  const completedStopCount = Number(task.completedStops || 0);
  const totalStopCount = Number(task.totalStops || stops.length || 0);
  const remainingStopCount = stops.filter((stop) => !FINAL_STOP_STATUSES.has(String(stop.status || "PENDING").toUpperCase())).length;

  async function withLocation(callback) {
    const result = await captureDriverLocation();
    try {
      await callback(result.location, result.warning);
    } catch {
      // Notification and recovery are handled by the shared action layer.
    }
  }

  const operationsPanel = (
    <DriverTaskOperations
      currentUser={currentUser}
      detail={detail}
      nextStop={nextStop}
      onChanged={onOperationalChanged}
      prominent={promoteOperations || guidedDeparturePhase}
      unsyncedCount={unsyncedCount}
    />
  );

  return (
    <div className="driver-page driver-task-detail-page">
      {!guidedLoadingPhase && (
        <section className="driver-detail-hero">
          <div>
            <span className={`driver-status-chip is-${statusTone(task.status)}`}>{task.statusLabel}</span>
            <h1>{task.foNumber}</h1>
            <p>{task.routeName}</p>
          </div>
          <div className="driver-detail-progress">
            <strong>{completedStopCount} dari {totalStopCount}</strong>
            <span>{remainingStopCount} titik tersisa</span>
          </div>
        </section>
      )}

      <DriverAmendmentAlert
        amendment={amendment}
        busy={actionBusy}
        onAcknowledge={onAcknowledgeAmendment}
      />

      {guidedLoadingPhase && (
        <LoadingPreparation
          busy={actionBusy}
          canConfirm={capabilities?.canConfirmLoading}
          canEdit={capabilities?.canEditLoading}
          execution={execution}
          guided
          onConfirm={onConfirmLoading}
          onSave={onSaveLoading}
          stops={stops}
          task={task}
        />
      )}

      {showOperationsEarly && operationsPanel}

      {stopExecutionPaused ? (
        <section className="driver-next-stop-card driver-overnight-resume-card">
          <span className="driver-eyebrow">Hari sebelumnya telah ditutup</span>
          <h2>Lanjutkan pengiriman hari berikutnya</h2>
          <p>{overnight.hotelName ? `Tempat menginap: ${overnight.hotelName}` : "Mulai hari baru sebelum menjalankan titik pengiriman berikutnya."}</p>
          <div className="driver-next-stop-actions driver-next-stop-actions--single">
            <button className="driver-primary-button" disabled={actionBusy || !capabilities?.canContinueTrip} onClick={onContinueTrip} type="button"><Icon name="route" size={18} /> Lanjutkan pengiriman hari berikutnya</button>
          </div>
        </section>
      ) : allStopsFinal ? (
        <section className="driver-next-stop-card driver-all-stops-complete-card">
          <span className="driver-complete-card-icon"><Icon name="check" size={24} /></span>
          <div>
            <span className="driver-eyebrow">Seluruh titik memiliki hasil akhir</span>
            <h2>Pengiriman selesai</h2>
            <p>{phase === "POST_TRIP_CHECK"
              ? "Lanjutkan pemeriksaan akhir perjalanan untuk menutup Fleet Order."
              : "Akhiri perjalanan setelah kendaraan benar-benar tiba di gudang."}</p>
          </div>
        </section>
      ) : tripExecutionPhase ? (
        <section className={`driver-next-stop-card ${activeStop ? "is-active" : ""}`}>
          <span className="driver-eyebrow">{activeStop ? "Sedang dituju" : "Tujuan berikutnya"}</span>
          <h2>{nextStop?.customerName || "Belum ada titik pengiriman"}</h2>
          <p>{nextStop ? `${nextStop.area || "Area belum tersedia"}${nextStop.salesNames?.length ? ` · Sales: ${nextStop.salesNames.join(", ")}` : ""}` : "Periksa isi Fleet Order pada Route Planning."}</p>
          {nextRouteMetricLabel && <div className="driver-next-stop-metrics"><Icon name="clock" size={16} /><span>{nextRouteMetricLabel}</span><small>Perkiraan rute</small></div>}
          <div className="driver-next-stop-actions">
            <button className="driver-secondary-button" disabled={mapLoading} onClick={onOpenMap} type="button"><Icon name="map" size={18} /> {mapLoading ? "Memuat..." : "Lihat peta rute"}</button>
            {nextStop && !activeStop && (
              <button className="driver-primary-button" disabled={actionBusy || !capabilities?.canExecuteStops} onClick={() => withLocation((location, warning) => onStartStop(nextStop.key, location, warning))} type="button"><Icon name="route" size={18} /> Mulai menuju lokasi</button>
            )}
            {activeStop && (
              <button className="driver-primary-button" disabled={actionBusy} onClick={() => setDialog({ mode: "complete", stop: activeStop })} type="button"><Icon name="check" size={18} /> Selesaikan pengiriman</button>
            )}
          </div>
          {!activeStop && detail.deliveryReporting?.morningRequired && (
            <div className="driver-warning-banner driver-morning-report-guard"><Icon name="document" size={17} /> Kirim atau teruskan laporan pagi sebelum menuju titik pertama.</div>
          )}
          {activeStop && (
            <div className="driver-next-stop-secondary-actions">
              <a
                aria-disabled={!googleMapsUrl(activeStop)}
                className="driver-secondary-button"
                href={googleMapsUrl(activeStop) || undefined}
                rel="noreferrer"
                target="_blank"
              >
                <Icon name="map" size={17} /> Navigasi Google Maps
              </a>
              <button className="driver-danger-button" disabled={actionBusy} onClick={() => setDialog({ mode: "fail", stop: activeStop })} type="button">
                Gagal atau tunda
              </button>
            </div>
          )}
        </section>
      ) : null}

      {!showOperationsEarly && promoteOperations && operationsPanel}

      <details className="driver-task-information">
        <summary>
          <span><Icon name="info" size={18} /> Informasi Fleet Order</span>
          <span className="driver-task-information__aside">
            <small>{task.vehiclePlate || "Armada belum dipilih"} · {task.documentCount} dokumen</small>
            <Icon name="chevronRight" size={17} />
          </span>
        </summary>
        <section className="driver-summary-grid" aria-label="Ringkasan Fleet Order">
          <article><Icon name="location" size={19} /><span>Berangkat dari</span><strong>{task.depotName || "Depot belum dipilih"}</strong><small>Titik awal perjalanan</small></article>
          <article><Icon name="route" size={19} /><span>Kembali ke</span><strong>{task.depotName || "Depot belum dipilih"}</strong><small>Titik akhir perjalanan</small></article>
          <article><Icon name="truck" size={19} /><span>Armada</span><strong>{task.vehiclePlate || "—"}</strong></article>
          <article><Icon name="user" size={19} /><span>Co-driver</span><strong>{task.coDriverName || "Tanpa co-driver"}</strong></article>
          <article><Icon name="clock" size={19} /><span>Estimasi pulang</span><strong>{task.estimatedReturnDateLabel || "—"}</strong></article>
          <article><Icon name="orders" size={19} /><span>Dokumen</span><strong>{task.documentCount} dokumen</strong></article>
        </section>
      </details>

      {emergencyLoadingPhase && (
        <LoadingPreparation
          busy={actionBusy}
          canConfirm={capabilities?.canConfirmLoading}
          canEdit={capabilities?.canEditLoading}
          execution={execution}
          onConfirm={onConfirmLoading}
          onSave={onSaveLoading}
          stops={stops}
          task={task}
        />
      )}

      {!showOperationsEarly && !promoteOperations && !guidedLoadingPhase && operationsPanel}

      {(tripExecutionPhase || stopExecutionPaused || allStopsFinal || new Set(["RETURNING", "POST_TRIP_CHECK"]).has(phase)) && (
        <section className="driver-section driver-stop-section">
          <div className="driver-section-heading">
            <div>
              <h2>Urutan pengiriman</h2>
              <p>{stopExecutionPaused
                ? "Lanjutkan hari terlebih dahulu sebelum menjalankan titik berikutnya."
                : tripExecutionPhase
                  ? "Ikuti urutan berikut. Pilih titik lain hanya bila diperlukan."
                  : execution?.phaseLabel}</p>
            </div>
            <span>{completedStopCount} selesai · {remainingStopCount} tersisa</span>
          </div>
          <div className="driver-stop-list">
            {stops.length ? stops.map((stop) => (
              <button
                aria-label={`Lihat detail titik ${stop.sequence}, ${stop.customerName}`}
                className={`driver-stop-row is-${statusTone(stop.status)}`}
                key={stop.key}
                onClick={() => setSelectedStopPreview(stop)}
                type="button"
              >
                <span className="driver-stop-marker">{statusIcon(stop.status) ? <Icon name={statusIcon(stop.status)} size={16} /> : stop.sequence}</span>
                <span className="driver-stop-copy">
                  <strong>{stop.customerName}</strong>
                  <small>{[stop.area, stop.documentNumbers?.join(", ")].filter(Boolean).join(" · ") || "Detail belum tersedia"}</small>
                  {stop.salesNames?.length > 0 && <small>Sales: {stop.salesNames.join(", ")}</small>}
                  {stop.notes && <small className="driver-stop-note">{stop.notes}</small>}
                </span>
                <span className="driver-stop-row-aside">
                  <span className="driver-stop-status">{stopStatusLabel(stop.status)}</span>
                  <span className="driver-stop-details-button">Lihat detail <Icon name="chevronRight" size={14} /></span>
                </span>
              </button>
            )) : <div className="driver-task-empty"><strong>Belum ada titik</strong><p>Fleet Order ini belum memiliki pelanggan aktif.</p></div>}
          </div>
        </section>
      )}

      <FloatingRouteAction
        busy={actionBusy || routeWorkspaceLoading}
        canReorder={Boolean(capabilities?.canReorder) && !allStopsFinal}
        count={remainingRouteCount}
        onOpen={onOpenRouteWorkspace}
      />

      {selectedStopPreview && (
        <DriverActionDialog
          actions={(
            <>
              <button className="driver-secondary-button driver-dialog-full-width-action" onClick={() => setSelectedStopPreview(null)} type="button">Tutup</button>
              {tripExecutionPhase
                && !stopExecutionPaused
                && !activeStop
                && new Set(["PENDING", "DEFERRED"]).has(String(selectedStopPreview.status || "PENDING").toUpperCase())
                && selectedStopPreview.key !== nextStop?.key && (
                  <button
                    className="driver-primary-button"
                    disabled={actionBusy}
                    onClick={() => {
                      setNextChangeTarget(selectedStopPreview);
                      setSelectedStopPreview(null);
                    }}
                    type="button"
                  >
                    Jadikan tujuan berikutnya
                  </button>
                )}
            </>
          )}
          description={`${selectedStopPreview.area || "Area belum tersedia"} · ${stopStatusLabel(selectedStopPreview.status)}`}
          onClose={() => setSelectedStopPreview(null)}
          title={`${selectedStopPreview.sequence}. ${selectedStopPreview.customerName}`}
        >
          <div className="driver-stop-detail-dialog">
            <dl>
              <div><dt>Dokumen</dt><dd>{selectedStopPreview.documentNumbers?.join(", ") || "Belum tersedia"}</dd></div>
              <div><dt>Sales</dt><dd>{selectedStopPreview.salesNames?.join(", ") || "Belum tersedia"}</dd></div>
              <div><dt>Alamat</dt><dd>{selectedStopPreview.addressText || selectedStopPreview.addressAlias || "Belum tersedia"}</dd></div>
            </dl>
            {activeStop && selectedStopPreview.key !== activeStop.key && (
              <div className="driver-info-note">Selesaikan atau tunda {activeStop.customerName} terlebih dahulu sebelum mengubah tujuan.</div>
            )}
            {!activeStop && selectedStopPreview.key === nextStop?.key && new Set(["PENDING", "DEFERRED"]).has(String(selectedStopPreview.status || "PENDING").toUpperCase()) && (
              <div className="driver-info-note">Titik ini sudah menjadi tujuan berikutnya.</div>
            )}
          </div>
        </DriverActionDialog>
      )}

      {nextChangeTarget && (
        <DriverActionDialog
          actions={(
            <>
              <button className="driver-secondary-button" disabled={actionBusy} onClick={() => setNextChangeTarget(null)} type="button">Batal</button>
              <button
                className="driver-primary-button"
                disabled={actionBusy}
                onClick={() => Promise.resolve(onMakeNext(nextChangeTarget.key)).then(() => {
                  setNextChangeTarget(null);
                  setSelectedStopPreview(null);
                }).catch(() => {})}
                type="button"
              >
                Ubah tujuan
              </button>
            </>
          )}
          description={`${nextStop?.customerName || "Tujuan saat ini"} akan digantikan sebagai tujuan berikutnya.`}
          onClose={() => setNextChangeTarget(null)}
          title="Ubah tujuan berikutnya?"
        >
          <div className="driver-route-change-confirmation">
            <span className="driver-eyebrow">Tujuan baru</span>
            <strong>{nextChangeTarget.customerName}</strong>
            <p>Urutan tersisa akan diperbarui. Titik yang sudah selesai tidak berubah.</p>
          </div>
        </DriverActionDialog>
      )}

      {dialog && (
        <DeliveryDialog
          busy={actionBusy}
          currentUser={currentUser}
          fleetOrderId={task.id}
          fleetOrderNumber={task.foNumber}
          mode={dialog.mode}
          onClose={() => setDialog(null)}
          onSubmit={(payload) => withLocation((location, warning) => {
            const done = dialog.mode === "complete"
              ? onCompleteStop(dialog.stop.key, { ...payload, ...location }, warning)
              : onFailStop(dialog.stop.key, { ...payload, ...location }, warning);
            Promise.resolve(done).then(() => setDialog(null)).catch(() => {});
          })}
          stop={dialog.stop}
          stopSequence={stops.findIndex((item) => item.key === dialog.stop.key) + 1}
          stopTotal={stops.length}
          vehiclePlate={task.vehiclePlate}
        />
      )}
    </div>
  );
}

